'use client'

import { useCallback, useId, useSyncExternalStore } from 'react'

// El augment global de `Window.YT`/`onYouTubeIframeAPIReady` vive en
// `./types.ts` (única fuente, evita declaraciones incompatibles duplicadas).

/** Compartido por todas las instancias del hook en la página — la API de
 * YouTube es un recurso global único, no por componente. */
/** Instancia (`useId`) a la que le toca renderizar el único `<Script>`. */
let scriptOwner: string | null = null
let callbackRegistered = false
const readyListeners = new Set<() => void>()

function isApiReady(): boolean {
  return typeof window !== 'undefined' && Boolean(window.YT?.Player)
}

/** Se registra una sola vez: cuando YouTube confirma que la API está lista,
 * notifica a todos los hooks que estaban esperando y vacía la cola. */
function registerGlobalReadyCallback(): void {
  if (callbackRegistered || typeof window === 'undefined') return
  callbackRegistered = true

  const previous = window.onYouTubeIframeAPIReady
  window.onYouTubeIframeAPIReady = () => {
    previous?.()
    readyListeners.forEach((listener) => listener())
    readyListeners.clear()
  }
}

/**
 * Carga perezosa (una sola vez para toda la página) del script global de la
 * YouTube IFrame Player API. `enabled` debe ser `false` cuando ningún item
 * del carrusel tiene vídeo — evita cargar el script sin necesidad. El
 * consumidor renderiza `<Script>` (`next/script`) solo cuando
 * `shouldRenderScript` es `true`, para no inyectar la etiqueta más de una
 * vez. Compartido por `HeroCarousel` y `Video` — ver
 * `specs/040-video-module/research.md` §3.
 */
export function useYouTubeIframeApi(enabled: boolean): {
  ready: boolean
  shouldRenderScript: boolean
} {
  const id = useId()

  // La API de YouTube es un sistema externo: se lee con `useSyncExternalStore`
  // en vez de copiarla a estado desde un efecto (evita renders en cascada).
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!enabled) return () => {}

      registerGlobalReadyCallback()
      readyListeners.add(onChange)

      // El primero que se suscribe con la API aún sin cargar se queda el
      // `<Script>`; `useSyncExternalStore` relee el snapshot tras suscribir.
      if (!scriptOwner && !isApiReady()) scriptOwner = id

      return () => {
        readyListeners.delete(onChange)
      }
    },
    [enabled, id],
  )

  const ready = useSyncExternalStore(subscribe, isApiReady, () => false)
  const shouldRenderScript = useSyncExternalStore(
    subscribe,
    () => scriptOwner === id,
    () => false,
  )

  return { ready, shouldRenderScript }
}
